/**
 * The rule tree — slice 67. One grammar for every place a person says "people
 * where …": a rule segment's definition, the segment builder's live preview, and
 * a Flow condition node.
 *
 * ## THE SHAPE
 *
 * ```
 * RuleNode = { kind: 'group', id, join: 'and' | 'or', children: RuleNode[] }   // 1..20 children, depth ≤ 3
 *          | { kind: 'rule',  id, field, op, value? }
 * ```
 *
 * The root is always a group, so "one rule" and "five rules" are the same shape
 * and the builder never has to promote a leaf into a group under somebody's
 * cursor. `id`s are the client's (the builder needs stable keys for its rows and
 * the preview's per-rule counts); the server only requires them unique.
 *
 * ## WHY THE VALUE IS VALIDATED BY OPERATOR HERE, AND BY TYPE ELSEWHERE
 *
 * The leaf is a discriminated union on `op`, so a value of the wrong SHAPE —
 * `between` with one number, `in_last` with no unit, `any_of` with an empty
 * list — cannot be represented at all, and fails parsing with the rule's path.
 * What this file cannot know is the field's TYPE: `extras.Joining date` is a
 * date in one workspace and does not exist in another. That needs the catalogue,
 * so it is {@link ruleTreeIssues}, which takes a resolver and is called by the
 * API (422 naming the field) and by the builder (inline, before a request is
 * sent) — one function, so the two cannot disagree about which operators a type
 * accepts.
 *
 * The operator ids are the concept's (`sampleData.js`), extended where the
 * owner's brief needed more: dates gain `not_in_last`, `between` and
 * `anniversary_in_next` (birthday campaigns over ANY date column); events say
 * `ever` / `never` rather than overloading `known`.
 *
 * ## WHAT THIS FILE DOES NOT DO
 *
 * Compile. The SQL compiler is `apps/api/src/modules/fields/rule-compiler.ts`,
 * and it binds every value as a parameter and looks every field key up in the
 * catalogue first — a key that reaches SQL has been whitelisted, and a value is
 * never interpolated. Nothing here builds a string of SQL.
 *
 * A LEAF: this module imports only zod, so `fields.ts`, `segment.ts` and
 * `flow.ts` can all import it without closing a cycle.
 */
import { z } from 'zod';

/* -------------------------------------------------------------------------- */
/* Limits                                                                      */
/* -------------------------------------------------------------------------- */

/** Children per group. */
export const RULE_MAX_CHILDREN = 20;
/** Group nesting: the root is depth 1, a group inside it 2, one more 3. */
export const RULE_MAX_DEPTH = 3;
/** Leaves across the whole tree — a guard against a malformed request, not a product limit. */
export const RULE_MAX_LEAVES = 60;
/** Values in one `any_of`. */
export const RULE_MAX_ANY_OF = 50;
/** One text value. */
export const RULE_TEXT_MAX = 200;
/** A catalogue key: `name`, `extras.<column>`, `derived:<key>`, `outcome:<uuid>`, … */
export const FIELD_KEY_MAX = 240;

/* -------------------------------------------------------------------------- */
/* Field types and operators                                                   */
/* -------------------------------------------------------------------------- */

/**
 * What a rule can be written over. `email` and `phone` columns are `text` here
 * (their samples are masked by the catalogue); `event` is the activity fields —
 * "replied on WhatsApp", "clicked a link", an outcome — which have a when but no
 * value.
 */
export const FIELD_TYPE_VALUES = ['text', 'number', 'boolean', 'date', 'enum', 'event'] as const;
export const fieldTypeSchema = z.enum(FIELD_TYPE_VALUES);
export type FieldType = z.infer<typeof fieldTypeSchema>;

export const RULE_OPERATOR_VALUES = [
  'gt',
  'gte',
  'lt',
  'lte',
  'eq',
  'between',
  'in_last',
  'not_in_last',
  'before',
  'after',
  'anniversary_in_next',
  'is',
  'is_not',
  'contains',
  'any_of',
  'is_true',
  'is_false',
  'known',
  'unknown',
  'ever',
  'never',
] as const;
export const ruleOperatorSchema = z.enum(RULE_OPERATOR_VALUES);
export type RuleOperator = z.infer<typeof ruleOperatorSchema>;

/** Which operators each type offers, in the order the builder lists them. */
export const OPERATORS_BY_TYPE: Readonly<Record<FieldType, readonly RuleOperator[]>> = {
  number: ['gt', 'gte', 'lt', 'lte', 'eq', 'between', 'known', 'unknown'],
  date: ['in_last', 'not_in_last', 'before', 'after', 'between', 'anniversary_in_next', 'known', 'unknown'],
  text: ['is', 'is_not', 'contains', 'any_of', 'known', 'unknown'],
  enum: ['is', 'is_not', 'any_of', 'known', 'unknown'],
  boolean: ['is_true', 'is_false', 'known', 'unknown'],
  event: ['in_last', 'not_in_last', 'ever', 'never'],
};

export function operatorAcceptsType(op: RuleOperator, type: FieldType): boolean {
  return OPERATORS_BY_TYPE[type].includes(op);
}

/** Operators that take no value. */
export const VALUELESS_OPERATORS = ['known', 'unknown', 'is_true', 'is_false', 'ever', 'never'] as const satisfies readonly RuleOperator[];

export function operatorNeedsValue(op: RuleOperator): boolean {
  return !(VALUELESS_OPERATORS as readonly RuleOperator[]).includes(op);
}

/**
 * The words for an operator, by type — "is in the last" on a date, "happened in
 * the last" on an event. `sym` is the compact form the segment summary line and
 * a Flow condition's label use.
 */
export function operatorLabel(op: RuleOperator, type: FieldType): { label: string; sym: string } {
  switch (op) {
    case 'gt':
      return { label: 'is greater than', sym: '>' };
    case 'gte':
      return { label: 'is at least', sym: '≥' };
    case 'lt':
      return { label: 'is less than', sym: '<' };
    case 'lte':
      return { label: 'is at most', sym: '≤' };
    case 'eq':
      return { label: 'equals', sym: '=' };
    case 'between':
      return { label: 'is between', sym: 'between' };
    case 'in_last':
      return type === 'event'
        ? { label: 'happened in the last', sym: 'in last' }
        : { label: 'is in the last', sym: 'in last' };
    case 'not_in_last':
      return type === 'event'
        ? { label: 'has not happened in the last', sym: 'not in last' }
        : { label: 'is not in the last', sym: 'not in last' };
    case 'before':
      return { label: 'is before', sym: 'before' };
    case 'after':
      return { label: 'is after', sym: 'after' };
    case 'anniversary_in_next':
      return { label: 'comes round in the next', sym: 'anniversary in' };
    case 'is':
      return { label: 'is', sym: '=' };
    case 'is_not':
      return { label: 'is not', sym: '≠' };
    case 'contains':
      return { label: 'contains', sym: 'contains' };
    case 'any_of':
      return { label: 'is any of', sym: 'any of' };
    case 'is_true':
      return { label: 'is true', sym: 'true' };
    case 'is_false':
      return { label: 'is false', sym: 'false' };
    case 'known':
      return { label: 'is known', sym: 'known' };
    case 'unknown':
      return { label: 'is unknown', sym: 'unknown' };
    case 'ever':
      return { label: 'has ever happened', sym: 'ever' };
    case 'never':
      return { label: 'has never happened', sym: 'never' };
  }
}

/* -------------------------------------------------------------------------- */
/* Values                                                                      */
/* -------------------------------------------------------------------------- */

/** Client-generated, stable per row. */
export const ruleIdSchema = z.string().regex(/^[A-Za-z0-9_-]{1,64}$/, 'A rule id is 1–64 letters, digits, - or _');

/**
 * A catalogue key. Shape only: whether it EXISTS in this workspace is the
 * catalogue's question ({@link ruleTreeIssues}). Control characters are refused
 * because a key is shown back in an error sentence.
 */
export const fieldKeySchema = z
  .string()
  .min(1)
  .max(FIELD_KEY_MAX)
  // eslint-disable-next-line no-control-regex
  .refine((key) => !/[\u0000-\u001f\u007f]/.test(key), 'A field key cannot contain control characters');

/** A calendar date, `YYYY-MM-DD`, that exists. Local to this leaf on purpose. */
export const ruleDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'A date must be written YYYY-MM-DD')
  .refine((value) => {
    const [y, m, d] = value.split('-').map(Number) as [number, number, number];
    const date = new Date(Date.UTC(y, m - 1, d));
    return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
  }, 'That is not a real date');

export const RULE_WINDOW_UNIT_VALUES = ['days', 'months'] as const;
export const ruleWindowUnitSchema = z.enum(RULE_WINDOW_UNIT_VALUES);
export type RuleWindowUnit = z.infer<typeof ruleWindowUnitSchema>;

/** "In the last 30 days", "not in the last 6 months". */
export const ruleWindowSchema = z
  .object({
    amount: z.number().int().min(1).max(3650),
    unit: ruleWindowUnitSchema,
  })
  .strict();
export type RuleWindow = z.infer<typeof ruleWindowSchema>;

const ruleTextValueSchema = z.string().trim().min(1, 'Type a value').max(RULE_TEXT_MAX);

/* -------------------------------------------------------------------------- */
/* Leaves — a discriminated union on the operator                              */
/* -------------------------------------------------------------------------- */

const leafBase = {
  kind: z.literal('rule'),
  id: ruleIdSchema,
  field: fieldKeySchema,
};

const compareLeaf = z
  .object({ ...leafBase, op: z.enum(['gt', 'gte', 'lt', 'lte', 'eq']), value: z.number().finite() })
  .strict();

/**
 * `between` is inclusive at both ends and takes either two numbers or two dates —
 * which of the two is the field type's business ({@link ruleTreeIssues}). Low
 * must not exceed high: a reversed range matches nobody, and silently swapping
 * it would be the builder deciding what the person meant.
 */
const betweenLeaf = z
  .object({
    ...leafBase,
    op: z.literal('between'),
    value: z.union([z.tuple([z.number().finite(), z.number().finite()]), z.tuple([ruleDateSchema, ruleDateSchema])]),
  })
  .strict()
  .refine((leaf) => {
    const [low, high] = leaf.value;
    return typeof low === 'number' ? low <= Number(high) : low <= String(high);
  }, {
    message: 'The first value must not be greater than the second',
    path: ['value'],
  });

const windowLeaf = z
  .object({ ...leafBase, op: z.enum(['in_last', 'not_in_last']), value: ruleWindowSchema })
  .strict();

const dateLeaf = z.object({ ...leafBase, op: z.enum(['before', 'after']), value: ruleDateSchema }).strict();

/**
 * The day and month of a date field fall within the next `days` days, from
 * today in IST — "birthday in the next 7 days" over `dob`, `date_of_birth`,
 * "Policy start" or any date column. 0 means today. A 29 February counts on
 * 28 February in a non-leap year (the compiler's rule, stated here so the copy
 * can say it).
 */
const anniversaryLeaf = z
  .object({
    ...leafBase,
    op: z.literal('anniversary_in_next'),
    value: z.object({ days: z.number().int().min(0).max(366) }).strict(),
  })
  .strict();

const textLeaf = z.object({ ...leafBase, op: z.enum(['is', 'is_not', 'contains']), value: ruleTextValueSchema }).strict();

const anyOfLeaf = z
  .object({
    ...leafBase,
    op: z.literal('any_of'),
    value: z
      .array(ruleTextValueSchema)
      .min(1, 'Pick at least one value')
      .max(RULE_MAX_ANY_OF)
      .refine((values) => new Set(values.map((v) => v.toLowerCase())).size === values.length, 'Each value only once'),
  })
  .strict();

/** No value: `value` is absent or null, never anything else. */
const valuelessLeaf = z
  .object({ ...leafBase, op: z.enum(VALUELESS_OPERATORS), value: z.null().optional() })
  .strict();

/**
 * The leaf. `between` is wrapped in a refine (ZodEffects), which a
 * discriminatedUnion cannot hold, so the union is on the other seven and
 * `between` is tried beside it.
 */
export const ruleLeafSchema = z.union([
  z.discriminatedUnion('op', [compareLeaf, windowLeaf, dateLeaf, anniversaryLeaf, textLeaf, anyOfLeaf, valuelessLeaf]),
  betweenLeaf,
]);
export type RuleLeaf = z.infer<typeof ruleLeafSchema>;

/* -------------------------------------------------------------------------- */
/* Groups and the tree                                                         */
/* -------------------------------------------------------------------------- */

export const RULE_JOIN_VALUES = ['and', 'or'] as const;
export const ruleJoinSchema = z.enum(RULE_JOIN_VALUES);
export type RuleJoin = z.infer<typeof ruleJoinSchema>;

export interface RuleGroup {
  kind: 'group';
  id: string;
  join: RuleJoin;
  children: RuleNode[];
}
export type RuleNode = RuleLeaf | RuleGroup;

export const ruleGroupSchema: z.ZodType<RuleGroup> = z.lazy(() =>
  z
    .object({
      kind: z.literal('group'),
      id: ruleIdSchema,
      join: ruleJoinSchema,
      children: z.array(ruleNodeSchema).min(1, 'A group needs at least one rule').max(RULE_MAX_CHILDREN),
    })
    .strict(),
);

export const ruleNodeSchema: z.ZodType<RuleNode> = z.lazy(() =>
  z.union([ruleLeafSchema, ruleGroupSchema]),
);

/** Every leaf, depth-first, with the ids of the groups above it. */
export function ruleLeaves(tree: RuleGroup): { leaf: RuleLeaf; path: string[] }[] {
  const out: { leaf: RuleLeaf; path: string[] }[] = [];
  const walk = (group: RuleGroup, path: string[]) => {
    for (const child of group.children) {
      if (child.kind === 'group') walk(child, [...path, child.id]);
      else out.push({ leaf: child, path });
    }
  };
  walk(tree, [tree.id]);
  return out;
}

/** Every group, root first. */
export function ruleGroups(tree: RuleGroup): RuleGroup[] {
  const out: RuleGroup[] = [];
  const walk = (group: RuleGroup) => {
    out.push(group);
    for (const child of group.children) if (child.kind === 'group') walk(child);
  };
  walk(tree);
  return out;
}

/** The distinct field keys a tree reads — what "is this derived field in use?" asks. */
export function ruleTreeFieldKeys(tree: RuleGroup): string[] {
  return [...new Set(ruleLeaves(tree).map(({ leaf }) => leaf.field))];
}

function depthOf(group: RuleGroup): number {
  let deepest = 0;
  for (const child of group.children) if (child.kind === 'group') deepest = Math.max(deepest, depthOf(child));
  return deepest + 1;
}

/**
 * A whole rule tree: a group at the root, at most {@link RULE_MAX_DEPTH} deep,
 * at most {@link RULE_MAX_LEAVES} rules, every id distinct (the preview's
 * per-rule counts are keyed on them).
 */
export const ruleTreeSchema = ruleGroupSchema.superRefine((tree, ctx) => {
  if (depthOf(tree) > RULE_MAX_DEPTH) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `Groups can be nested ${RULE_MAX_DEPTH} deep at most`,
    });
  }
  const leaves = ruleLeaves(tree);
  if (leaves.length > RULE_MAX_LEAVES) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: `A segment can have ${RULE_MAX_LEAVES} rules at most` });
  }
  const ids = [...ruleGroups(tree).map((g) => g.id), ...leaves.map(({ leaf }) => leaf.id)];
  const seen = new Set<string>();
  for (const id of ids) {
    if (seen.has(id)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `The id “${id}” is used twice in one rule tree` });
      break;
    }
    seen.add(id);
  }
});
export type RuleTree = RuleGroup;

/* -------------------------------------------------------------------------- */
/* Against the catalogue                                                       */
/* -------------------------------------------------------------------------- */

export const RULE_ISSUE_CODE_VALUES = [
  'unknown_field',
  'operator_not_for_type',
  'value_not_for_type',
  'needs_date_format',
] as const;
export type RuleIssueCode = (typeof RULE_ISSUE_CODE_VALUES)[number];

export const ruleIssueSchema = z
  .object({
    code: z.enum(RULE_ISSUE_CODE_VALUES),
    ruleId: ruleIdSchema,
    field: z.string(),
    /** A sentence naming the field, ready to show. */
    message: z.string().min(1),
  })
  .strict();
export type RuleIssue = z.infer<typeof ruleIssueSchema>;

/**
 * What the catalogue says about one key, for {@link ruleTreeIssues}. `null` when
 * the workspace has no such field (or it is hidden).
 */
export interface RuleFieldInfo {
  type: FieldType;
  label: string;
  /** A CSV date column whose day/month order nobody has chosen yet. */
  needsDateFormat?: boolean;
}

/**
 * The catalogue half of validation: unknown keys, operators the type does not
 * offer, `between` with dates on a number (or the reverse), and date rules over
 * a column whose dd/mm order is still unanswered. The API turns any issue into
 * a 422 naming the field; the builder shows the same sentence inline.
 */
export function ruleTreeIssues(tree: RuleGroup, resolve: (fieldKey: string) => RuleFieldInfo | null | undefined): RuleIssue[] {
  const issues: RuleIssue[] = [];
  for (const { leaf } of ruleLeaves(tree)) {
    const info = resolve(leaf.field);
    if (!info) {
      issues.push({
        code: 'unknown_field',
        ruleId: leaf.id,
        field: leaf.field,
        message: `This workspace has no field called “${leaf.field}”. It may have been renamed, hidden or removed with its upload.`,
      });
      continue;
    }
    if (!operatorAcceptsType(leaf.op, info.type)) {
      issues.push({
        code: 'operator_not_for_type',
        ruleId: leaf.id,
        field: leaf.field,
        message: `“${info.label}” is a ${info.type} field and cannot use “${operatorLabel(leaf.op, info.type).label}”.`,
      });
      continue;
    }
    if (leaf.op === 'between') {
      const datesGiven = typeof leaf.value[0] === 'string';
      if (datesGiven !== (info.type === 'date')) {
        issues.push({
          code: 'value_not_for_type',
          ruleId: leaf.id,
          field: leaf.field,
          message: `“${info.label}” is a ${info.type} field, so “between” needs two ${info.type === 'date' ? 'dates' : 'numbers'}.`,
        });
        continue;
      }
    }
    if (info.type === 'date' && info.needsDateFormat && leaf.op !== 'known' && leaf.op !== 'unknown') {
      issues.push({
        code: 'needs_date_format',
        ruleId: leaf.id,
        field: leaf.field,
        message: `“${info.label}” holds dates like 03/04/2026. Choose whether they are day-first or month-first before using it in a rule — Wend never guesses.`,
      });
    }
  }
  return issues;
}

/** A one-line summary of a leaf, for lists and condition labels: "Age > 25". */
export function ruleLeafSummary(leaf: RuleLeaf, info: Pick<RuleFieldInfo, 'label' | 'type'> | null): string {
  const label = info?.label ?? leaf.field;
  const type = info?.type ?? 'text';
  const { sym } = operatorLabel(leaf.op, type);
  const value = (() => {
    switch (leaf.op) {
      case 'between':
        return `${String(leaf.value[0])}–${String(leaf.value[1])}`;
      case 'in_last':
      case 'not_in_last':
        return `${leaf.value.amount} ${leaf.value.unit}`;
      case 'anniversary_in_next':
        return `${leaf.value.days} days`;
      case 'any_of':
        return leaf.value.join(', ');
      case 'gt':
      case 'gte':
      case 'lt':
      case 'lte':
      case 'eq':
        return leaf.value.toLocaleString('en-IN');
      case 'before':
      case 'after':
      case 'is':
      case 'is_not':
      case 'contains':
        return leaf.value;
      default:
        return '';
    }
  })();
  return [label, sym, value].filter((part) => part !== '').join(' ');
}
